/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { forwardRef } from 'react';

import { cssMap, jsx } from '@compiled/react';

import { token } from '@atlaskit/tokens';
import TooltipPrimitive, { type TooltipPrimitiveProps } from '@atlaskit/tooltip/TooltipPrimitive';

const styles = cssMap({
	base: {
		boxSizing: 'border-box',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		maxWidth: '240px',
		backgroundColor: token('elevation.rovo.surface.overlay.hovered'),
		borderWidth: token('border.width'),
		borderStyle: 'solid',
		borderColor: token('color.border.inverse'),
		boxShadow: token('elevation.shadow.overflow'),
		color: token('color.text'),
		font: token('font.body.small'),
		overflowWrap: 'break-word',
		wordWrap: 'break-word',
		paddingBlock: token('space.100'),
		paddingInline: token('space.150'),
	},
	notchBottomLeft: {
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.xlarge'),
		borderBottomLeftRadius: token('radius.small'),
	},
	notchBottomRight: {
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.small'),
		borderBottomLeftRadius: token('radius.xlarge'),
	},
	notchTopLeft: {
		borderTopLeftRadius: token('radius.small'),
		borderTopRightRadius: token('radius.xlarge'),
		borderBottomRightRadius: token('radius.xlarge'),
		borderBottomLeftRadius: token('radius.xlarge'),
	},
	notchTopRight: {
		borderTopLeftRadius: token('radius.xlarge'),
		borderTopRightRadius: token('radius.small'),
		borderBottomRightRadius: token('radius.xlarge'),
		borderBottomLeftRadius: token('radius.xlarge'),
	},
});

/**
 * Passed as the suggestion right-surface button's `tooltip.component` to replace
 * `@atlaskit/tooltip`'s default bubble with this one.
 */
export const SuggestionTooltip: React.ForwardRefExoticComponent<
	React.PropsWithoutRef<TooltipPrimitiveProps> & React.RefAttributes<HTMLDivElement>
> = forwardRef<HTMLDivElement, TooltipPrimitiveProps>(function SuggestionTooltip(
	{ style, className, children, placement, testId, onMouseOut, onMouseOver, id, shortcut, role },
	ref,
) {
	const isBelowTrigger = placement?.startsWith('bottom');
	const isEndAligned = placement?.endsWith('end');

	return (
		<TooltipPrimitive
			ref={ref}
			style={style}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop
			className={className}
			placement={placement}
			testId={testId}
			id={id}
			onMouseOut={onMouseOut}
			onMouseOver={onMouseOver}
			shortcut={shortcut}
			role={role}
			// Compiled's `css` prop must stay a literal array/logical-expression for its babel transform to statically extract classNames
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props
			css={[
				styles.base,
				!isBelowTrigger && !isEndAligned && styles.notchBottomLeft,
				!isBelowTrigger && isEndAligned && styles.notchBottomRight,
				isBelowTrigger && !isEndAligned && styles.notchTopLeft,
				isBelowTrigger && isEndAligned && styles.notchTopRight,
			]}
		>
			{children}
		</TooltipPrimitive>
	);
});
